import React, { useMemo, useRef, useState, useEffect, useReducer, useCallback } from 'react';
import { connect, getLocale, history } from 'umi';
import { PagingQuery, Button, Modal } from 'tea-component';
import { cloneDeep } from 'lodash-es';
import Filter from './components/Filter';
import Visualis from './components/Visualis';
import Search from './components/Search';
import Table from './components/Table';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import {
  getGlobalViewPageDataWhenOther,
  getGlobalViewRegionDataWhenOther,
  getGlobalViewMarketDataByMobile,
  getGenreWhenMobile
} from '@/pages/intelligence/services/globalView';
import { getCountryFlag } from '@/pages/home/_config/countries';
// import { renderImgText } from '@/pages/intelligence/components/TeaSelector/MultipleSelector';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buildGenresReqParams, getSortKey, getDefaultYears } from './utils';
import { getFilterListRegions as getRegionsWhenMobile } from '@/pages/intelligence/services/filter';
import { generateVisualisAndTablesMetadata } from './utils/analysisData';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';
import { MAXGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import {
  PER_TYPES,
  REVENUE_TYPES,
  REGION_MARKET_TYPES,
  GENRE_TYPES,
  GLOBAL_VIEW_METRICS_OPTS,
  PAGE_LOG_CONFIG_KEYS,
  PAGE_GLOBAL_CONTEXT_NAME
} from './constants';
import type {
  IFilter,
  IAction,
  IPerType,
  IMetrics,
  IVisualisAndTablesMetadata,
  ISelectorOpt,
  ITableAction,
  IDebounce,
  ITableParamsAction,
  IListDataItem,
  IBarItem,
  IInterfaceResult,
  IOriginalPageData,
  IGenreItem
} from './types';

import AllCountry from '@/pages/intelligence/assets/svg/dataSource/all-country.svg';
import './index.scss';

import useCancelableServices from './hooks/useCancelableServices';
import { CANCEL_FLAG_MSG } from '@intelligence/utils/constant';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_WHEN_REGION = 11;
const DEFAULT_PAGE_OPTIONS = [10, 15, 20];
const DEFAULT_PAGE_INDEX = 1;
const DEFAULT_SORT_ORDER = 'desc';
const RANK_MAP = ['1st', '2nd', '3rd'];
const KEY_COLUMN_MAP: any = {
  market_region: {
    name: 'Region',
    rankName: 'Genre',
  },
  market_market: {
    name: 'Core market',
    rankName: 'Genre',
  },
  genre_region: {
    name: 'Genre',
    rankName: 'Region',
  },
  genre_market: {
    name: 'Genre',
    rankName: 'Markets',
  },
};

const filterReducer = (state: IFilter, { type, payload }: IAction) => {
  return {
    ...state,
    [type]: payload
  };
}

const tableActionReducer = (state: ITableAction, { type, payload }: IAction) => {
  return {
    ...state,
    [type]: payload
  };
}

const isBarDataComposeable = (barData: any[]): boolean => {
  if (!Array.isArray(barData)) return false;
  return barData?.every((item) => item?.list?.length < 10);
};

const composeBarData = (barData: any[], waittingComposedBarData: any[]): any[] => {
  if (!isBarDataComposeable(barData)) return barData;
  const barDataTmp = cloneDeep(barData);
  for (const waittingComposedBarDataItem of waittingComposedBarData) {
    const date = waittingComposedBarDataItem?.date;
    const list = waittingComposedBarDataItem?.list || [];
    const target = barDataTmp?.find((barDataTmpItem: any) => barDataTmpItem?.date === date);
    const targetList = target?.list || [];
    const targetKeys = targetList?.map((targetItem: any) => targetItem?.name) || [];
    for (const item of list) {
      const itemName = item?.name;
      if (targetKeys?.includes(itemName)) continue;
      targetList.push(item);
      targetKeys.push(itemName);
    }
  }
  return barDataTmp;
};

const composePieData = (pieData: any[], waittingComposedPieData: any[]): any[] => {
  if (pieData?.length >= 10) return pieData;
  const pieDataTmp = cloneDeep(pieData);
  const pieDataTmpKeys = pieDataTmp?.map((pieDataItem: any) => pieDataItem?.name);
  for (const waittingComposedPieDataItem of waittingComposedPieData) {
    const waittingComposedPieDataItemName = waittingComposedPieDataItem?.name;
    if (pieDataTmpKeys?.includes(waittingComposedPieDataItemName)) continue;
    pieDataTmp.push(waittingComposedPieDataItem);
    pieDataTmpKeys.push(waittingComposedPieDataItemName);
  }
  return pieDataTmp;
};

const composeOriginalResData = (originalData: any, waittingComposedOriginalData: any): any => {
  let result;
  try {
    return {
      ...originalData,
      barData: composeBarData(originalData?.barData, waittingComposedOriginalData?.barData),
      pieData: composePieData(originalData?.pieData, waittingComposedOriginalData?.pieData),
    };
  } catch (exception) {
    console.warn(exception);
    result = null;
  }
  return result;
};

const filterBarData = (barData: any[], waittingFilteredBarData: any[]): any[] => {
  const barDataTmp = cloneDeep(barData);
  const waittingFilteredBarDataKeys = waittingFilteredBarData?.map((targetItem: any) => targetItem?.name) || [];
  for (const barDataItem of barDataTmp) {
    const list = barDataItem?.list || [];
    for (let i = list?.length - 1; i >= 0; i--) {
      const item = list?.[i];
      const itemIdx = waittingFilteredBarDataKeys?.indexOf(item?.name);
      if (itemIdx === -1) continue;
      list.splice(i, 1);
    }
  }
  return barDataTmp;
};

const filterPieData = (pieData: any[], waittingFilteredPieData: any[]): any[] => {
  const pieDataTmp = cloneDeep(pieData);
  const pieDataTmpKeys = pieDataTmp?.map((pieDataItem: any) => pieDataItem?.name);
  for (const waittingFilteredPieDataItem of waittingFilteredPieData) {
    const waittingFilteredPieDataIdx = pieDataTmpKeys?.indexOf(waittingFilteredPieDataItem?.name);
    if (waittingFilteredPieDataIdx === -1) continue;
    pieDataTmp.splice(waittingFilteredPieDataIdx, 1);
  }
  return pieDataTmp;
};

const filterOriginalResData = (originalData: any, waittingFilteredOriginalData: any) => {
  let result;
  try {
    return {
      ...originalData,
      barData: filterBarData(originalData?.barData, waittingFilteredOriginalData),
      pieData: filterPieData(originalData?.pieData, waittingFilteredOriginalData),
    };
  } catch (exception) {
    console.warn(exception);
    result = null;
  }
  return result;
};

const CompositePage = (props: any) => {
  const { perType, revenueType, dispatch: globalDispatch, context } = props;

  const {
    getGlobalViewTabletDataWhenMobile,
    getGlobalViewChartsDataWhenMobile,
  } = useCancelableServices();

  const debounce = useRef<IDebounce>({ interval: undefined, delay: 500 }).current;
  const [isCheckboxInit, setIsCheckboxInit] = useState<boolean>(false);
  // 筛选器
  const [years, setYears] = useState([]);
  const [regionOpts, setRegionOpts] = useState<any[]>([]);
  const [marketOpts, setMarketOpts] = useState<any[]>([]);
  const [genreOpts, setGenreOpts] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<IMetrics>('revenue');
  const cachedFilter = useRef<{
    regionType: string;
    genreType: string;
    [key: string]: any;
  }>({
    regionType: perType !== context.lastPerType ? REGION_MARKET_TYPES.REGION : context.filter.regionType,
    genreType: perType !== context.lastPerType ? GENRE_TYPES.MAIN : context.filter.genreType,
  });
  const [filter, filterDispatch] = useReducer(filterReducer, {
    regionType: perType !== context.lastPerType ? REGION_MARKET_TYPES.REGION : context.filter.regionType,
    years: [],
    genreType: perType !== context.lastPerType ? GENRE_TYPES.MAIN : context.filter.genreType,
    dateType: 'month',
    dateValue: ['', ''],
    genres: [],
    mainGenres: [],
    subGenres: [],
    regions: [],
    markets: [],
  });

  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const [tableAction, tableActionDispatch] = useReducer(tableActionReducer, {
    // 表格当前页码
    pageIndex: DEFAULT_PAGE_INDEX,
    // 表格一页展示条数
    pageSize: perType === PER_TYPES.MARKET ? DEFAULT_PAGE_SIZE_WHEN_REGION : DEFAULT_PAGE_SIZE,
    // 排序的字段
    sortKey: '',
    // 排序的方式，升序或者降序
    sortOrder: DEFAULT_SORT_ORDER,
  });

  const [uiDatas, setUIDatas] = useState<IVisualisAndTablesMetadata>({
    bars: [],
    pies: [],
    tables: [],
    barsLegends: [],
    piesLegends: []
  });
  // 存储页面信息
  const [pageInfo, setPageInfo] = useState(null);
  // 表格loading
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);
  // 可视化loading
  const [isChartLoading, setIsChartLoading] = useState<boolean>(true);
  // 存储接口返回的原始数据，用于Revenue和Downloads的切换
  const [origianlResData, setOrigianlResData] = useState<IOriginalPageData>({});

  const [tableData, setTableData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [pageSizeOptions, setPageSizeOptions] = useState<number[]>([perType === PER_TYPES.MARKET ? DEFAULT_PAGE_SIZE_WHEN_REGION : DEFAULT_PAGE_SIZE,]);
  // 表格的指标列名，目前为3列
  const [tableMetricsColumns, setTableMetricsColumns] = useState<{ title: string; simpleTitle: string }[]>([]);
  const [tableSelected, setTableSelected] = useState<IListDataItem[]>([]);
  const [tableSelectedAll, setTableSelectedAll] = useState<IListDataItem[]>([]);

  // 判断当时是否是手游
  const isMobile = useMemo(() => {
    return revenueType === REVENUE_TYPES.MOBILE;
  }, [revenueType]);

  const isRegionPage = useMemo(() => {
    return filter?.regionType === REGION_MARKET_TYPES.REGION;
  }, [filter.regionType]);

  const isTableNameCanBeClick = useCallback((name, type) => {
    // 当core market，且为国家时，才可能被点击
    const marketCond = perType === PER_TYPES.MARKET && type === 'name' && !['Global', 'global'].includes(name);
    const genreCond = perType === PER_TYPES.GENRE && type === 'metric' && !['Global', 'global'].includes(name);
    return !isRegionPage && (marketCond || genreCond);
  }, [isRegionPage]);

  // 表格中是否展示环比
  const isDodHide = useMemo(() => {
    let [startDate, endDate] = filter?.years || [];
    if (isMobile) return false;
    if (!endDate) endDate = startDate;
    const startDateNum = Number(startDate);
    const endDateNum = Number(endDate);
    if (startDateNum === 2021 && endDateNum === 2021) return true;
    if (endDateNum - startDateNum >= 1) return true;
    return false;
  }, [filter?.years, isMobile]);

  // 表格是否展示checkbox列
  const isTableCheckboxHide = useMemo(() => filter?.regionType !== REGION_MARKET_TYPES.MARKET, [filter?.regionType]);

  // 是否为搜索状态中，搜索状态中的勾选，不更新originalResData
  const isSearching = useRef<boolean>(false);
  // 搜索结果只有一个，联动勾选该唯一的搜索结果时，缓存tableSelected值，用于取消搜索后，恢复搜索前的勾选状态
  const tableSelectedCached = useRef<IListDataItem[]>([]);
  const tableSelectedAllCached = useRef<IListDataItem[]>([]);
  // 获取备份的勾选状态（用于取消搜索后 恢复勾选状态）
  const getTableSelectedCached = (searchResults: any) => {
    const correctTableSelected = isSearching?.current ? tableSelectedCached?.current : tableSelected;
    // 当前勾选状态
    let tableSelectedTmp = correctTableSelected?.slice(0);
    // 没有勾选交互时，直接返回当前勾选状态
    if (isTableCheckboxHide) return tableSelectedTmp;
    // 当前勾选状态达到上限时，直接返回当前勾选状态
    if (tableSelectedTmp?.length >= 10) return tableSelectedTmp;
    // 当前搜索结果
    const searchResult = searchResults?.[0];
    const tableSelectedKeys = tableSelected?.map((item: any) => item?.name?.toLowerCase());
    if (tableSelectedKeys?.includes(searchResult?.name?.toLowerCase())) return tableSelectedTmp;
    return [
      ...tableSelectedTmp,
      searchResult,
    ];
  };
  const handleAutoSearchSelect = (searchResult: any) => {
    if (isTableCheckboxHide) return;
    if (!Array.isArray(searchResult?.list) || !searchResult?.list?.length) return;
    const tableSelectedTmp = searchResult?.list?.slice(0, 1);
    tableSelectedCached.current = getTableSelectedCached(searchResult?.list);
    if (!isSearching?.current) {
      tableSelectedAllCached.current = tableSelectedAll?.slice(0);
    }
    isSearching.current = true;
    setTableSelected(tableSelectedTmp);
    setTableSelectedAll(tableSelectedTmp);
    handleTableCheckboxChange(tableSelectedTmp, 'replace');
  };

  const handleFilterChange = ({ type, payload }: IAction) => {
    // 两次值相同则不触发更新，这个会导致偶现的 不调用接口，一直loading问题，可以先注释了，目前没发现有重复调用接口的问题
    // if (filter?.[type] === payload || (Array.isArray(payload) && JSON.stringify(payload) === JSON.stringify(filter?.[type]))) return;
    // 级联品类筛选器做了特殊处理
    if (type === 'cascadeGenres') {
      filterDispatch({ type: 'mainGenres', payload: payload.mainGenres });
      filterDispatch({ type: 'subGenres', payload: payload.subGenres });
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilter`,
        key: 'mainGenres',
        payload: payload.mainGenres
      })
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilter`,
        key: 'subGenres',
        payload: payload.subGenres
      })
    } else {
      filterDispatch({ type, payload });
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilter`,
        key: type,
        payload: payload
      })
    }
    // if (['regionType', 'genreType'].includes(type)) return;
    if (['regionType', 'genreType'].includes(type)) {
      cachedFilter.current[type] = payload;
      // per market页面 修改 regionType时，不调initial方法，修改regionType会关联触发regions、和 markets，修改genreType时没有后续的关联触发change，所以修改genreType时 需要调用initial方法
      if (perType === PER_TYPES.MARKET && type === 'regionType') {
        // 市场切换regionType重置筛选器选中标记
        globalDispatch({
          type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilterChangeBeforeFlag`,
          payload: false
        })
        return;
      }
      // per genre页面 修改 genreType时，不调initial方法，修改genreType会关联触发genres、mainGenres和 subGenres，修改regionType时没有后续的关联触发change，所以修改genreType时 需要调用initial方法
      if (perType === PER_TYPES.GENRE && type === 'genreType') return;
    } else if (['genres', 'regions', 'markets', 'cascadeGenres'].includes(type) && !context.isFilterChangeBefore) {
      // 只有当筛选器选项发生变化后，才会启动继承逻辑
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilterChangeBeforeFlag`,
        payload: true
      });
    }
    if (['genres', 'regions', 'markets', 'cascadeGenres'].includes(type)) {
      // 只要发生勾选变化，就清空上一次选中，避免一直继承问题
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastSelectedMetric`,
        payload: null
      });
    }
    initial('filter', false, false, {
      [type]: payload,
      ...cachedFilter.current,
      pageIndex: DEFAULT_PAGE_INDEX,
    });
  };

  // 缓存最后一次组装接口请求入参的结果，用于搜索（由于搜索组件内 有useEffect导致 获取不到最新的参数，需要用这个ref来实现获取到最新的参数）
  const cachedParams = useRef<any>({});

  // 获取接口请求参数
  const getParams = (type?: 'chart' | 'table', action?: ITableParamsAction) => {
    const pageIndex = action?.pageIndex || tableAction.pageIndex;
    const pageSize = action?.pageSize || tableAction.pageSize;
    const sortKey = action?.sortKey || tableAction.sortKey;
    const sortOrder = action?.sortOrder || tableAction.sortOrder;
    const selected = action?.tableSelected || [];
    const years = action?.years || filter?.years || [];
    const regionType = action?.regionType || filter?.regionType;
    const regions = action?.regions || filter?.regions || [];
    const markets = action?.markets || filter?.markets || [];
    const dateValue = action?.dateValue || filter?.dateValue || [];
    const genres = action?.genres || filter?.genres;
    const mainGenres = action?.cascadeGenres?.mainGenres || filter?.mainGenres || [];
    const subGenres = action?.cascadeGenres?.subGenres || filter?.subGenres || [];
    const metric = action?.metrics || metrics;
    const _regionOpts = action?.regionOpts || regionOpts || [];
    const _marketOpts = action?.marketOpts || marketOpts || [];

    const getNotMobileParams = () => {
      // 国家 需要把筛选器返回的code 转换为 对应的name
      const transformRegionMarketsForReq = (filter: IFilter, isRegion: boolean): string[] => {
        let list: string[] = [];
        let options: any[] = [];
        if (isRegion) {
          list = regions;
          options = _regionOpts;
        } else {
          list = markets;
          options = _marketOpts;
        }
        return list?.map((item) => {
          const target = options?.find(option => option?.value === item);
          return target?.label || item;
        }) || [];
      };

      // 后端使用metrics一个字段去存储，可能是regions/markets/genres
      const _metrics = ((): string[] => {
        // 品类直接返回筛选器的选择的品类
        if (perType === PER_TYPES.GENRE) return genres;
        // 国家 需要把筛选器返回的code 转换为 对应的name
        return transformRegionMarketsForReq(filter, isRegionPage);
      })();
      return {
        per_type: perType,
        revenue_type: revenueType,
        region_type: regionType,
        dates: years,
        metrics: Array.isArray(_metrics) ? _metrics : [],
      };
    };
    const getMobileParams = () => {
      const genreType = action?.genreType || filter?.genreType;
      const [startDate = '', endDate = ''] = dateValue || [];
      const _params = {
        per_type: perType, // 标识是哪个页面 market/genre
        region_type: regionType,
        regions: isRegionPage ? regions || [] : markets || [],
        genre_type: genreType, // main sub main_sub
        genre: buildGenresReqParams({
          perType, filter: {
            genreType: genreType,
            genres,
            mainGenres,
            subGenres
          }
        }), // 品类筛选器
        metrics: [metric],
        period: 'month', // 只有月粒度
        start_date: startDate,
        end_date: endDate,
        order_metric: getSortKey(sortKey),
        order_type: sortOrder,
      };
      if (type === 'chart') {
        const _m = perType === PER_TYPES.MARKET ? 'name_abbr' : 'name';
        Object.assign(_params, {
          selected: selected?.map(item => item[_m]),
          is_not_select: !!action?.isNotChecked
        });
        // chart需要根据选择的项来进行展示，所以不需要页码
      } else {
        Object.assign(_params, {
          page: pageIndex,
          page_size: pageSize
        });
      }

      return _params;
    };
    const params = isMobile ? getMobileParams() : getNotMobileParams();;
    // 每次重新组装接口入参后，更新缓存的接口入参，用于搜索时获取页面参数用
    cachedParams.current = params;
    return params;
  };

  // 搜索时，获取页面的参数
  const getParamsForSearch = () => cachedParams.current;

  // 获取页面信息，主要是用于表格的表头变化
  const getPageInfo = () => {
    const key = `${perType}_${filter?.regionType}`;
    return KEY_COLUMN_MAP[key];
  };

  // 表头需要根据metrics的变化而变化
  const getTableMetricsColumns = (_info: any, _metrics?: string) => {
    const m = GLOBAL_VIEW_METRICS_OPTS.find(item => item.value === (_metrics || metrics))?.text || 'Revenue';
    return RANK_MAP.map((rankStr) => {
      return {
        title: `${rankStr} ${_info.rankName} & ${m}`,
        simpleTitle: m,
      };
    });
  }

  // 获取表格指标列的数据，目前固定是三列
  // 设置初始化的配置，如表格的字段列，初始排序
  const setDefaultConfig = (_metrics?: string) => {
    const _pageInfo = getPageInfo();
    const metricsColumns = getTableMetricsColumns(_pageInfo, _metrics);
    setPageInfo(_pageInfo);
    tableActionDispatch({ type: 'sortKey', payload: `${metricsColumns?.[0]?.title}_0` });
    setTableMetricsColumns(metricsColumns);
  }

  const getCurrentPageSize = () => {
    return isRegionPage && perType === PER_TYPES.MARKET && !isMobile ? DEFAULT_PAGE_SIZE_WHEN_REGION : DEFAULT_PAGE_SIZE;
  }

  // 从接口中获取，并设置筛选器的数据，主要包括region、market和genre
  const getFilterData = async () => {
    // 手游比较特殊,和手游榜单的region和country保持一致,直接平铺
    const getRegionsOrMarkets = (_result: any[], _textStr: string, _textCNStr: string, _valueStr: string, isNeedToAddGlobal?: boolean) => {
      const _values: string[] = [];
      const _valuesOpts: ISelectorOpt[] = [];
      _result.forEach((item: any) => {
        const text = item[_textStr];
        const textCN = item[_textCNStr];
        _values.push(item[_valueStr]);
        _valuesOpts.push({
          text,
          value: item[_valueStr],
          label: text,
          labelCN: textCN,
          tooltip: text,
        })
        // 如果选项没有global，需要追加global选项
        if (!_values?.includes('global') && isNeedToAddGlobal) {
          _values.unshift('global');
          _valuesOpts.unshift({ text: 'Global', value: 'global', label: 'Global', labelCN: '全球' });
        }
      });
      return { _values, _valuesOpts }
    }
    return new Promise(async (resolve, reject) => {
      if (isMobile) {
        if (perType === PER_TYPES.MARKET) {
          const resultRegionWhenMobile = await getRegionsWhenMobile({
            filter_type: 'common',
            type: [{ tabName: 'mobile', }],
            item: [{ tabName: '', }],
          });
          const regionCountry = resultRegionWhenMobile || [];
          const { _values: regionValues, _valuesOpts: regionOpts } = getRegionsOrMarkets(regionCountry, 'area_name_en', 'area_name_ch', 'area_code', true);
          const _regionOpts = getOptionsWithCountryIcon(regionOpts, true);
          setRegionOpts(_regionOpts);
          // filterDispatch({ type: 'regions', payload: regionValues });

          // 获取市场接口不用同步调用
          await getGlobalViewMarketDataByMobile().then(resultMarketWhenMobile => {
            const { _values: marketValues, _valuesOpts: marketOpts } = getRegionsOrMarkets(resultMarketWhenMobile?.data || [], 'country_name_en', 'country_name_ch', 'country_code');
            const _marketOpts = getOptionsWithCountryIcon(marketOpts, false);
            setMarketOpts(_marketOpts);
            // filterDispatch({ type: 'markets', payload: marketValues });
            resolve({
              regions: regionValues,
              regionOpts: _regionOpts,
              markets: marketValues,
              marketOpts: _marketOpts
            });
          }).catch(() => {
            resolve({
              regions: [],
              regionOpts: [],
              markets: [],
              marketOpts: []
            });
          })
        } else {
          // mobile的副品类、主+副品类 的品类选项数据 是再GenreSelector组件内 自行获取的（fetchAction配置获取选项数据的方法），所以这里直接resolve就好
          if (filter.genreType !== GENRE_TYPES.MAIN) return resolve({
            genres: [],
            genreOpts: [],
          });
          // 主品类时，在页面调用接口获取品类数据
          const result = await getGenreWhenMobile({
            genre_type: filter.genreType
          })
          const { genres = [], ieg = [] } = result?.data || {};
          const _genreOpts = genres.map((item: IGenreItem) => {
            const { name_en: nameEN, name_ch: nameCN, } = item ?? {};
            return {
              text: item,
              label: nameEN,
              labelCN: nameCN,
              value: nameEN
            };
          });
          setGenreOpts(_genreOpts);
          // filterDispatch({ type: 'genres', payload: genres });
          resolve({
            // genres: genres || [],
            genres: genres?.map((item: IGenreItem) => item?.name_en) ?? [],
            genreOpts: _genreOpts
          });
        }
      } else {
        const formatToKeyValue = (list: any) => {
          if (!Array.isArray(list)) return [];
          return list.map((item: any) => {
            const { name_en: nameEN, name_ch: nameCN } = item ?? {};
            return {
              text: nameEN,
              value: nameEN,
              label: nameEN,
              labelCN: nameCN,
              tooltip: isCN ? (nameCN || nameEN) : nameEN,
            };
          });
        };
        const resultOther = await getGlobalViewRegionDataWhenOther();
        const { dates, genres = [], markets = [], regions = [] } = resultOther?.data || {};
        const datesTmp = dates ?? getDefaultYears() ?? [];
        const yearsTmp = datesTmp?.slice(datesTmp?.length - 2) ?? [];
        const { _values: regionValues, _valuesOpts: regionValueOpts } = getRegionsOrMarkets(regions, 'country_name_en', 'country_name_ch', 'country_code');
        const { _values: marketValues, _valuesOpts: marketValueOpts } = getRegionsOrMarkets(markets, 'country_name_en', 'country_name_ch', 'country_code');
        const _genreOpts = formatToKeyValue(genres);
        setYears(datesTmp);
        setRegionOpts(getOptionsWithCountryIcon(regionValueOpts, true));
        setMarketOpts(getOptionsWithCountryIcon(marketValueOpts, false));
        setGenreOpts(_genreOpts);
        resolve({
          years: yearsTmp,
          yearOpt: dates,
          regions: regionValues,
          regionOpts: regionValueOpts,
          markets: marketValues,
          marketOpts: marketValueOpts,
          genres: genres?.map((item: IGenreItem) => item?.name_en),
          genreOpts: _genreOpts
        });
      }
    })
  };

  // 处理搜索后的数据
  const renderSearchData = (searchResults) => {
    const { list = [], chartList = [], pieList = [] } = searchResults || {};
    // if (cachedFilter.current?.regionType === REGION_MARKET_TYPES.REGION) {
    if (isMobile) {
      // 没数据的时候，设置可视化为空
      if (!list || !list.length) {
        renderChartData([], []);
      } else if (filter?.regionType === REGION_MARKET_TYPES.REGION) {
        // 如果有数据，设置可视化为选中的
        handleChartData({
          tableSelected: list,
        }, true);
      }
      renderTableData(list, list?.length, true)
    } else {
      // 非手游直接是一个接口返回
      triggerSetWhenOther('', list, chartList, pieList, { pageIndex: 1 }, true);
    }
    // } else {
    //   renderTableData(list, list?.length, true)
    // }
  };

  // 处理表格数据
  const renderTableData = (_tableData: IListDataItem[], total: number, isNotNeedToCheck?: boolean, _action?: ITableParamsAction) => {
    const setTableCheck = (_values: IListDataItem[]) => {
      setTableSelected(_values.slice(0, 10));
      // TODO 全选逻辑可能有问题，需要选中所有
      setTableSelectedAll(_values);
      setIsCheckboxInit(true);
    }

    setTableData(_tableData);
    setTotal(total);
    // 前端排序和前端分页，也就是非手游数据变化时，不需要重新选择
    if ((!isCheckboxInit || !isNotNeedToCheck) && !isRegionPage) {
      const checked = _action?.tableSelected || _tableData;
      setTableCheck(checked);
    }
    setIsTableLoading(false);
  }

  const renderChartData = (_barData: IBarItem[], _pieData: IListDataItem[]) => {
    const uiDatas = generateVisualisAndTablesMetadata(_barData, _pieData, {
      perType,
      revenueType,
      regionType: filter?.regionType,
    });
    setUIDatas(uiDatas);
    setIsChartLoading(false);
  }

  // 根据表格的顺序对饼图和柱状图进行排序
  const getShowChartDataWhenOther = (_tableData: IListDataItem[], _barData?: IBarItem[], _pieData?: IListDataItem[]) => {
    const _showPieData: IListDataItem[] = []
    const _showBarobj: {
      [key: string]: IBarItem
    } = {};
    if (!_barData) _barData = origianlResData.barData;
    if (!_pieData) _pieData = origianlResData.pieData;
    _tableData.forEach((_tableItem: IListDataItem) => {
      (_barData || []).forEach((_barYearItem: IBarItem) => {
        if (!_showBarobj.hasOwnProperty(_barYearItem.date)) {
          _showBarobj[_barYearItem.date] = {
            date: _barYearItem.date,
            list: []
          }
        }
        const find = _barYearItem?.list?.find((_barItem: IListDataItem) => _barItem?.name?.toLowerCase() === _tableItem?.name?.toLowerCase());
        if (find) _showBarobj[_barYearItem.date].list.push(find);
      })
      const findPie = (_pieData || []).find((_pieItem: IListDataItem) => _pieItem.name === _tableItem.name);
      if (findPie) _showPieData.push(findPie);
    })
    return {
      pieData: _showPieData,
      barData: Object.keys(_showBarobj).map(key => _showBarobj[key])
    }
  };

  // 对数组进行排序
  const getSortedTableData = (list: any, _sortKey?: string, _sortOrder?: string) => {
    if (!_sortKey) _sortKey = tableAction.sortKey;
    if (!_sortOrder) _sortOrder = tableAction.sortOrder;
    const len = _sortKey.length;
    // 获取排名数字
    const rank = Number(_sortKey.slice(0, 1));
    // 获取类型，是值还是环比值
    let type = _sortKey.slice(len - 1);
    // 初始化时由于异步，导致sortKey没携带0或者1，当初始化为环比会默认以dod排序，排序就会出问题
    if (!['0', '1'].includes(type)) {
      type = '0';
    }
    // 过滤方法，主要作用是将环比（dod）为空的和不为空的拆分不同数组
    const filter = () => {
      const isDodNullArr: any[] = [];
      const isDodNotNullArr: any[] = [];
      list.forEach((item: any) => {
        const _dod = item.metrics?.[rank - 1]?.dod;
        if (_dod === null) {
          isDodNullArr.push(item);
        } else {
          isDodNotNullArr.push(item);
        }
      });
      return {
        isDodNullArr,
        isDodNotNullArr,
      };
    };
    // 对比方法
    const compareFun = (a: any, b: any) => {
      const typeStr = type === '0' ? 'Value' : 'dod';
      const aValue = a.metrics?.[rank - 1]?.[typeStr];
      const bValue = b.metrics?.[rank - 1]?.[typeStr];
      return _sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    };
    // 如果是以值排序或者没有环比数据时，直接使用sort方法
    if (type === '0' || (type === '1' && !isDodHide)) {
      return list.sort(compareFun);
    }
    // 如果是以环比排序，需要拼接数组
    const { isDodNullArr, isDodNotNullArr } = filter();
    return isDodNotNullArr.sort(compareFun).concat(...isDodNullArr);
  };

  const handleChartData = (_action?: ITableParamsAction, isNotNeedToSetOrigin?: boolean, isComposeOriginalResData = false) => {
    return new Promise<IInterfaceResult[]>((resolve, reject) => {
      const params = getParams('chart', _action);
      const selected = params?.selected ?? [];
      getGlobalViewChartsDataWhenMobile(params).then(chartResult => {
        if (!chartResult?.data) {
          setIsChartLoading(false);
          renderChartData([], []);
          resolve([]);
          return;
        }
        resolve(chartResult?.data);
        let { chart_list, pie_list } = chartResult?.data[0];
        const comparater = (a: any, b: any): number => {
          const aIdx = selected?.indexOf(a?.name_abbr);
          const bIdx = selected?.indexOf(b?.name_abbr);
          return aIdx - bIdx;
        };
        const chatListTmp = chart_list?.map((item: any) => {
          // const listTmp = item?.list?.sort(comparater) ?? [];
          // mobile的 不需要前端排序
          let listTmp = item?.list || [];
          if (revenueType !== REVENUE_TYPES.MOBILE) {
            listTmp = listTmp?.sort(comparater) || [];
          }
          return {
            ...item,
            list: listTmp ?? [],
          };
        }) ?? [];
        // const pieListTmp = pie_list?.sort(comparater) ?? [];
        // mobile的 不需要前端排序
        let pieListTmp = pie_list || [];
        if (revenueType !== REVENUE_TYPES.MOBILE) {
          pieListTmp = pieListTmp?.sort(comparater) || [];
        }
        // 搜索不需要设置原始数据
        if (!isNotNeedToSetOrigin) {
          setOrigianlResData(Object.assign(origianlResData, {
            barData: chatListTmp,
            pieData: pieListTmp
          }));
        }
        if (isComposeOriginalResData) {
          const originalResDataTmp = composeOriginalResData(origianlResData, {
            barData: chatListTmp,
            pieData: pieListTmp,
          });
          if (originalResDataTmp !== null) {
            setOrigianlResData(originalResDataTmp);
          }
        }
        renderChartData(chatListTmp, pieListTmp);
      }).catch((error: any) => {
        if (error?.msg !== CANCEL_FLAG_MSG) {
          resolve([]);
          setIsChartLoading(false);
        }
      });
    });
  };

  // checkbox的回调
  const handleTableCheckboxChange = (list: any, status: string) => {
    let checked = list;
    switch (status) {
      case 'uncheck all':
        setTableSelected(list);
        break;
      case 'check all':
        if (tableSelected) {
          const topTen = list.slice(0, 10);
          checked = topTen;
          setTableSelected(topTen);
        } else {
          setTableSelected(list);
        }
        break;
      case 'uncheck':
        const removedArr = tableSelected.filter((item) => item?.name !== list.name);
        checked = removedArr;
        setTableSelected(removedArr);
        break;
      case 'check':
        const newArrt = tableSelected.concat(list);
        checked = newArrt;
        setTableSelected(newArrt);
        break;
      // 仅用于 搜索只有一个结果 并且 自动勾选该唯一搜索结果时
      case 'replace':
        checked = list;
        setTableSelected(list);
        break;
      default:
        break;
    }
    setIsChartLoading(true);
    if (!isMobile) {
      const { pieData, barData } = getShowChartDataWhenOther(getSortedTableData(checked));
      renderChartData(barData, pieData);
      return;
    }
    // 避免多次请求
    clearTimeout(debounce.interval);
    debounce.interval = setTimeout(() => {
      // const isNotNeedToSetOrigin = isSearching.current || status === 'replace';
      // handleChartData({
      //   tableSelected: checked,
      //   isNotChecked: !checked.length
      // }, isNotNeedToSetOrigin);

      const isNeedToSetOrigin = ['check', 'uncheck'].includes(status);
      const isComposeOriginalResData = status === 'replace' && tableSelected?.length < 10;
      handleChartData({
        tableSelected: checked,
        isNotChecked: !checked.length
      }, !isNeedToSetOrigin, isComposeOriginalResData);
    }, 1000)
  };

  // 获取icon的方法
  const getIcon = (item: IListDataItem, isRegionPagePayload = isRegionPage) => {
    const { name, name_abbr } = item;
    // if (name === 'Global') return AllCountry;
    if (['Global', '全球'].includes(name)) return AllCountry;
    // regionType为region的时候没有图标
    if (isRegionPagePayload) return null;
    if (name_abbr) return getCountryFlag(name_abbr);
  };

  // region、market 下拉选项option增加国旗icon
  const getOptionsWithCountryIcon = (options: any[], isRegion: boolean): any[] => {
    if (!Array.isArray(options)) return [];
    return options.map((option) => {
      const params: any = {
        name: option?.text,
        name_abbr: option?.value,
      };
      const icon = getIcon(params, isRegion);
      return {
        ...option,
        icon,
        // 保留原始name，pc、console、total的接口入参 需要name，不是code
        label: option?.text,
        // text: renderImgText(option?.text, icon),
      };
    });
  };

  // 获取最终显示在页面上的表格数据
  const getShowTableDataWhenOther = (_tableData: IListDataItem[], _action?: ITableParamsAction) => {
    // 当前页展示的表格数据
    const split = (list: any, _index: number, _size: number) => {
      if (!list) return [];
      const startingPoint = (_index - 1) * _size;
      const endingPoint = startingPoint + _size;
      return list.slice(startingPoint, endingPoint);
    };

    // 根据排序字段和排序方式排序
    const sort = (list: IListDataItem[], _action?: ITableParamsAction) => {
      const { sortKey, sortOrder } = _action || {};
      // 由于存在异步，参数携带过来，或者从tableAction去取。
      const _sortKey = sortKey || tableAction.sortKey;
      const _sortOrder = sortOrder || tableAction.sortOrder;
      return getSortedTableData(list, _sortKey, _sortOrder);
    }

    // 重新计算排名
    const resetRank = (list: IListDataItem[]) => {
      return list.map((item: IListDataItem, index: number) => ({
        ...item,
        rank: index,
      }))
    }

    _tableData = sort(_tableData, _action);
    _tableData = resetRank(_tableData);
    const _pageIndex = _action?.pageIndex || tableAction.pageIndex;
    const _pageSize = _action?.pageSize || tableAction.pageSize;
    return split(_tableData, _pageIndex, _pageSize);
  };

  // 搜索后的回填
  const backfillingAfterSearch = () => {
    const { tableData = [], barData = [], pieData = [], total = 0 } = origianlResData || {};
    const tableSelectedTmp = tableSelectedCached?.current?.slice(0) || [];
    const tableSelectedAllTmp = tableSelectedAllCached?.current?.slice(0) || [];
    if (isSearching.current) {
      setTableSelected(tableSelectedTmp);
      setTableSelectedAll(tableSelectedAllTmp);
    }
    if (isMobile) {
      // if (isRegionPage) {
      //   // 23.10.8 产品添加region页，搜索时可视化要跟随搜索结果
      //   renderTableData(tableData, total, true);
      //   renderChartData(barData, pieData);
      // } else {
      //   renderTableData(tableData, total, true);
      // }

      if (isSearching.current) {
        setIsChartLoading(true);
        handleTablePagingChange({ pageIndex: tableAction?.pageIndex || 1 }, tableSelectedTmp);
      } else {
        // 搜索增加联动勾选后，取消搜索 不区分region、core market 都需要重新渲染 可视化、表格
        renderTableData(tableData, total, true);
        renderChartData(barData, pieData);
      }
    } else {
      if (isRegionPage) {
        triggerSetWhenOther('', tableData, barData, pieData, {}, true);
      } else {
        // let showTableData = getShowTableDataWhenOther(tableData);
        // renderTableData(showTableData, tableData.length, true);

        // 搜索增加联动勾选后，取消搜索 core market 都需要重新渲染 可视化、表格
        const tableSelectedPayload = isSearching.current ? tableSelectedTmp : undefined;
        triggerSetWhenOther('', tableData, barData, pieData, {}, true, tableSelectedPayload);
      }
    }
    isSearching.current = false;
  };

  // 之前选中的checked数据都是原来的，如果直接使用排序方法，得到的排序结果会不一致
  const getSortSelected = (_tableData: IListDataItem[], selected: IListDataItem[]) => {
    const _arr: IListDataItem[] = [];
    _tableData.forEach((_tableItem) => {
      const find = selected.find(item => item.name === _tableItem.name);
      if (find) _arr.push(_tableItem);
    })
    return _arr;
  };

  const triggerSetWhenOther = (
    changeType: string,
    _tableData: IListDataItem[],
    _barData: IBarItem[],
    _pieData: IListDataItem[],
    _action?: ITableParamsAction,
    isNotNeedToCheck?: boolean,
    tableSelectedPayload = tableSelected,
  ) => {
    let showTableData = getShowTableDataWhenOther(_tableData, _action);
    renderTableData(showTableData, _tableData.length, isNotNeedToCheck, _action);
    // 将选中的数据排序后作为chart的数据,region页不用
    if (isNotNeedToCheck && !isRegionPage) {
      showTableData = getSortSelected(_tableData, tableSelectedPayload);
    }
    // 分页的时候，可视化不需要跟着表格数据变化
    if (changeType === 'page') return;
    const { barData: showBarData, pieData: showPieData } = getShowChartDataWhenOther(showTableData, _barData, _pieData);
    renderChartData(showBarData, showPieData);
  }

  const setPageData = async (changeType: string, isNeedToProcessData?: boolean, isNotNeedToCheck?: boolean, action?: ITableParamsAction) => {
    const appendAttr = (_isMobile: boolean, list: IListDataItem[], _action?: ITableParamsAction): IListDataItem[] => {
      // 添加属性
      const appendAttribute = (list: IListDataItem[] = []): IListDataItem[] => {
        if (!list) list = [];
        return list.map((item, index) => Object.assign(item, { rank: index, icon: getIcon(item) }));
      };
      return appendAttribute(list) || [];
    }

    if (isNeedToProcessData) {
      const { tableData = [], barData = [], pieData = [] } = origianlResData || {};
      triggerSetWhenOther(changeType, tableData, barData, pieData, action, isNotNeedToCheck);
    } else if (isMobile) {
      const params = getParams('table', action);
      // 这里拆开两步是因为两个接口互不影响，先渲染表格
      getGlobalViewTabletDataWhenMobile(params).then(tableResult => {
        if (!tableResult?.data) {
          setIsTableLoading(false);
          renderTableData([], 0);
          return;
        };
        let { metric, list, total } = tableResult?.data[0];
        const appendedTableData = appendAttr(isMobile, list, action);
        if (!total) total = 0;
        renderTableData(appendedTableData, total, isNotNeedToCheck, action);
        // 手游页面，初始化时间不选，等后端返回再填充
        if (!filter.dateValue[0]) {
          const { start_date = '', end_date = '' } = tableResult?.req || {};
          // 填充数据会触发重复渲染，而isInit是控制重复渲染的开关
          filterDispatch({ type: 'dateValue', payload: [start_date, end_date] });
          globalDispatch({
            type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilter`,
            key: 'dateValue',
            payload: [start_date, end_date]
          })
        }
        setOrigianlResData(Object.assign(origianlResData, {
          tableData: list,
          total: total
        }));
      }).catch((error: any) => {
        if (error?.msg !== CANCEL_FLAG_MSG) {
          setIsTableLoading(false);
        }
      });
      handleChartData(action)
    } else {
      getGlobalViewPageDataWhenOther(getParams('table', action)).then((result) => {
        if (!result?.data) {
          renderTableData([], 0);
          setIsTableLoading(false);
          renderChartData([], []);
          setIsChartLoading(false);
          return;
        }
        const { list, chart_list, pie_list } = result?.data;
        const appendedTableData = appendAttr(isMobile, list, action);
        triggerSetWhenOther(changeType, appendedTableData, chart_list, pie_list, action, isNotNeedToCheck)
        setOrigianlResData({
          tableData: appendedTableData,
          barData: chart_list,
          pieData: pie_list,
          total: total
        });
      }).catch(() => {
        setIsTableLoading(false);
        setIsChartLoading(false);
      });;
    }
  };


  // 页面初始化入口
  const initial = (changeType: string, isNeedToProcessData?: boolean, isNotNeedToCheck?: boolean, action?: ITableParamsAction) => {
    setIsTableLoading(true);
    if (changeType !== 'page') {
      setIsChartLoading(true);
    }
    // 避免多次请求
    clearTimeout(debounce.interval);
    debounce.interval = setTimeout(() => {
      setPageData(changeType, isNeedToProcessData, isNotNeedToCheck, action);
    }, debounce.delay)
  }

  // 当表格分页器改变时的回调
  const handleTablePagingChange = (e: PagingQuery, tableSelectedPayload = tableSelected) => {
    const { pageIndex = DEFAULT_PAGE_INDEX, pageSize = DEFAULT_PAGE_SIZE } = e;
    tableActionDispatch({ type: 'pageIndex', payload: pageIndex });
    tableActionDispatch({ type: 'pageSize', payload: pageSize });
    initial('page', !isMobile, true, {
      pageIndex,
      pageSize,
      tableSelected: tableSelectedPayload,
    });
  };

  // 排序的时候的回调
  const handleTableSortChange = (e: SortByItemType) => {
    const { key = '', sortVal = 'desc' } = e;
    tableActionDispatch({ type: 'sortKey', payload: key });
    tableActionDispatch({ type: 'sortOrder', payload: sortVal });
    tableActionDispatch({ type: 'pageIndex', payload: 1 });
    initial('sort', !isMobile, false, {
      sortKey: key,
      sortOrder: sortVal,
      pageIndex: 1,
    });
  };

  const getPageDefaultValue = (_metrics?: IMetrics): ITableParamsAction => {
    if (!_metrics) {
      _metrics = 'revenue';
    }
    return {
      pageIndex: DEFAULT_PAGE_INDEX,
      pageSize: getCurrentPageSize(),
      sortKey: getTableMetricsColumns(getPageInfo(), _metrics)[0].title,
      sortOrder: DEFAULT_SORT_ORDER,
      metrics: _metrics,
    }
  };

  const handleNameClick = (context: any, type: string) => {
    const { name, name_abbr } = context;
    if (!isTableNameCanBeClick(name, type)) return;
    const hostName = location.origin;
    const getMarketLinkUrl = () => {
      const getTargetPageId = () => {
        // total和mobile的pageid都是mobile
        let str = 'Mobile'
        switch (revenueType) {
          case 'pc':
            str = 'PC';
            break;
          case 'console':
            str = 'Console';
            break;
          default:
            break;
        }
        return `MarketProfil${str}`
      }
      return `/intelligence/MarketProfile/Overview/detail?marketName=${name}&marketNameEn=${name_abbr?.toLowerCase()}&pageId=${getTargetPageId()}`
    };
    // const getGenreLinkUrl = () => {
    //   const getGenreName = () => {
    //     // mainsub返回的name是主品类,副品类
    //     return isMobile && filter.genreType === 'main_sub' ? name.split(',')[1] : name;
    //   }

    //   const getGenreType = () => {
    //     return isMobile && ['main_sub', 'sub'].includes(filter.genreType) ? 'sub' : 'main';
    //   }
    //   return `${hostName}/intelligence_detail/yd/GenreProfile/GenreDetail/GenreInsights?entityType=${revenueType}&genre=${getGenreName()}&genreType=${getGenreType()}&source=iegg`;
    // }
    // window.location.replace(perType === PER_TYPES.MARKET ? getMarketLinkUrl() : getGenreLinkUrl());
    // window.location.replace(getMarketLinkUrl());
    history.push(getMarketLinkUrl())
  }

  // 只有手游的时候，revenue和downloads的segment切换
  // 数据无需从接口去取，而是从内存中取
  const handleMetricsChange = (_metrics: IMetrics) => {
    setMetrics(_metrics);
    resetTableAction();
    setDefaultConfig(_metrics);
    initial('metrics', false, false, getPageDefaultValue(_metrics));
  };

  const resetTableAction = () => {
    setTableSelected([]);
    setTableSelectedAll([]);
    setTableData([]);
    setOrigianlResData({});
    tableActionDispatch({
      type: 'pageIndex',
      payload: DEFAULT_PAGE_INDEX
    });
    tableActionDispatch({
      type: 'pageSize',
      payload: getCurrentPageSize()
    });
    setPageSizeOptions(isRegionPage && perType === PER_TYPES.MARKET && !isMobile ? [DEFAULT_PAGE_SIZE_WHEN_REGION] : DEFAULT_PAGE_OPTIONS);
    tableActionDispatch({
      type: 'sortOrder',
      payload: DEFAULT_SORT_ORDER
    });
  };

  const setFilterData = (filterValue: any) => {
    Object.keys(filterValue).forEach((key) => {
      if (!['regionOpts', 'marketOpts', 'genreOpts'].includes(key)) {
        filterDispatch({ type: key, payload: filterValue[key] });
        globalDispatch({
          type: `${PAGE_GLOBAL_CONTEXT_NAME}/setFilter`,
          key,
          payload: filterValue[key]
        })
      }
    })
  };

  /**
   * 筛选器继承逻辑
   * 1 revenueType切换（手游切换要注意genre的带入）
      1-1 market
        非手游 => 非手游 regionType继承 year继承 regions/market对比差异
        非手游 => mobile 继承年份，自动补齐收尾1,12月份 regions/market对比差异
        mobile => 非手游 继承年份 regions/market对比差异
      1-2 genre
        非手游 => 非手游 regionType继承 year继承 genre对比差异
        非手游 => mobile 继承年份，自动补齐收尾1,12月份 genre对比差异
        mobile => 非手游 继承年份 regions/market对比差异

    2 regionType切换
      2-1 market
        2-1-1 非手游
          region => core markets Year继承 Region不继承（恢复全选）
          core markets => region Year继承 market不继承（恢复全选）
        2-1-2手游
          region => core markets dateValue继承 Region不继承（恢复全选）
          core markets => region dateValue继承 market不继承（恢复全选）

      2-2 genre
        2-2-1 非手游
          region => core markets Year继承 genre对比差异
          core markets => region Year继承 genre对比差异
        2-2-2手游
          region => core markets dateValue继承 genre对比差异
          core markets => region dateValue继承 genre对比差异

    3 genreType切换
      3-1 market
        3-1-1 region
          dateValue继承 Region继承

        3-1-2 core markets
          dateValue继承 market继承

      3-2 genre
        3-2-1 region
          dateValue继承 genre不继承（从sub或者main_sub页面进入main页面，恢复全选）

        3-2-2 core markets
          dateValue继承 genre不继承（从sub或者main_sub页面进入main页面，恢复全选）
   */
  const inherit = (initialFilterValue: any, curRegionType: string, curGenreType: string) => {
    let needFilterValue = initialFilterValue || {};
    const _lastRegionType = context.lastRegionType;
    const _lastGenreType = context.lastGenreType;
    const _lastRevenueType = context.lastRevenueType;
    const contextFilter = context.filter;
    const isMarketPage = perType === PER_TYPES.MARKET;
    const isRegionType = (type: string) => {
      return type === REGION_MARKET_TYPES.REGION;
    }
    const isMobilePage = (type: string) => {
      return type === REVENUE_TYPES.MOBILE;
    }
    /**
     * 判断值是否在options里面，如果是则填入，如果为空则全选
     * @param keyArr 值，可以是regions/markets/genres
     * @param optArr options
     * @returns 值的缩写
     */
    const checkIn = (keyArr: any, optArr: any) => {
      if (!keyArr) keyArr = [];
      if (!optArr) optArr = [];
      // 假设有上一次选中，说明上一次是没有被继承的，就选中上一次选中的
      if (context.lastSelectedMetric) {
        keyArr = context.lastSelectedMetric;
        // 使用完及时清空
        globalDispatch({
          type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastSelectedMetric`,
          payload: null
        })
      }
      const _tmpArr: any[] = [];
      keyArr.forEach((key: string) => {
        const find = optArr.find((item: any) => item?.value === key);
        if (find) {
          _tmpArr.push(key);
        }
      })
      // 当筛选器没有改动过，或者没继承到的时候，全选
      if (!context.isFilterChangeBefore || !_tmpArr.length) {
        // 恢复默认时，记录上一次选中
        globalDispatch({
          type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastSelectedMetric`,
          payload: keyArr
        })
        return optArr.map((item: any) => item.value);
      }
      return _tmpArr;
    }
    const getRegionInherit = () => {
      return {
        regions: checkIn(contextFilter.regions, needFilterValue?.regionOpts),
        regionOpts: needFilterValue?.regionOpts || []
      }
    }
    const getMarketInherit = () => {
      return {
        markets: checkIn(contextFilter.markets, needFilterValue.marketOpts),
        marketOpts: needFilterValue.marketOpts,
      }
    }
    const getGenreInherit = () => {
      return {
        genres: checkIn(contextFilter.genres, needFilterValue.genreOpts),
        genreOpts: needFilterValue.genreOpts,
      }
    }
    const getYearInherit = () => {
      return {
        years: contextFilter.years,
      }
    }
    const getDateInherit = () => {
      return {
        dateValue: contextFilter.dateValue,
      }
    }
    const getFilterValueWhenRevenueTypeChange = () => {
      // 继承手游的年份，如2021/1/1-2023/1/1，则继承2021-2023
      const getInheritYear = (filterDate: string[], fetchData: any) => {
        const { years: filterYears, yearOpt } = fetchData;
        const [startDate, endDate] = filterDate;
        const [startYear] = startDate.split('-');
        const [endYear] = endDate.split('-');
        const num = Number(endYear) - Number(startYear) + 1;
        let years = [];
        for (let i = 0; i < num; i++) {
          const _year = (Number(startYear) + i).toString();
          // 防止手游选中的时间在非手游中不存在
          if (yearOpt.includes(_year)) {
            years.push(_year);
          }
        }
        // 如果全部未选中，就用默认
        if (!years.length) {
          years = filterYears;
        }
        return {
          years: years
        }
      };
      // 手游继承非手游的年份，补齐月份，如2021-2023，则变为2021-01, 2023-12
      const getInheritYearAndMonth = (filterYear: string[]) => {
        const _startYear = filterYear[0];
        const _endYear = filterYear.length === 1 ? _startYear : filterYear[filterYear.length - 1];
        const dateArr = [`${_startYear}-01-01`, `${_endYear}-12-01`];
        return {
          years: [],
          dateValue: dateArr
        }
      }
      const getInheritMetirc = () => {
        return isMarketPage
          ? isRegionType(curRegionType)
            ? getRegionInherit()
            : getMarketInherit()
          : getGenreInherit();
      };
      // 非手游 => 非手游 regionType继承 year继承 regions/market/genre对比差异
      if (!isMobilePage(_lastRevenueType) && !isMobilePage(revenueType)) {
        return {
          ...getYearInherit(),
          ...getInheritMetirc()
        }
        // 非手游 => 手游 继承年份，自动补齐收尾1,12月份 regions/market/genre对比差异
      } else if (!isMobilePage(_lastRevenueType) && isMobilePage(revenueType)) {
        return {
          ...getInheritMetirc(),
          ...getInheritYearAndMonth(contextFilter.years)
        }
        // 手游 => 非手游 继承年份 regions/market/genre对比差异
      } else if (isMobilePage(_lastRevenueType) && !isMobilePage(revenueType)) {
        return {
          ...getInheritYear(contextFilter.dateValue, initialFilterValue),
          ...getInheritMetirc()
        }
      }
    }
    const getFilterValueWhenRegionTypeChange = () => {
      // 不继承时获取区域或者市场
      const getRegionOrMarketWhenNotInherit = () => {
        // 从region进入core Markets
        const enterMarketFromRegion = isRegionType(_lastRegionType) && !isRegionType(curRegionType);
        return enterMarketFromRegion ? {
          regions: [],
          markets: checkIn([], needFilterValue?.marketOpts)
        } : {
          markets: [],
          regions: checkIn([], needFilterValue?.regionOpts)
        }
      }
      if (isMarketPage) {
        return !isMobilePage(revenueType) ? {
          // region => core markets Year继承 Region不继承（恢复全选）
          // core markets => region Year继承 market不继承（恢复全选）
          ...getYearInherit(),
          ...getRegionOrMarketWhenNotInherit(),
        } : {
          // region => core markets dateValue继承 Region不继承（恢复全选）
          // core markets => region dateValue继承 market不继承（恢复全选）
          ...getDateInherit(),
          ...getRegionOrMarketWhenNotInherit(),
        }
      }
      return !isMobilePage(revenueType) ? {
        // region => core markets Year继承 genre对比差异
        // core markets => region Year继承 genre对比差异
        ...getYearInherit(),
        ...getGenreInherit()
      } : {
        // region => core markets dateValue继承 genre对比差异
        // core markets => region dateValue继承 genre对比差异
        ...getDateInherit(),
        ...getGenreInherit()
      }
    }
    const getFilterValueWhenGenreTypeChange = () => {
      // 从sub或者main_sub页面进入main页面
      const enterMainFromSubOrMainSub = [GENRE_TYPES.SUB, GENRE_TYPES.MAIN_SUB].includes(_lastGenreType) && curGenreType === GENRE_TYPES.MAIN;
      if (isMarketPage) {
        // dateValue继承 Region/markets继承
        return {
          ...getDateInherit(),
          ...isRegionType(curRegionType) ? getRegionInherit() : getMarketInherit()
        }
      }
      // dateValue继承 genre不继承（从sub或者main_sub页面进入main页面，恢复全选）
      return {
        ...getDateInherit(),
        ...enterMainFromSubOrMainSub ? getGenreInherit() : {
          genre: [],
          mainGenres: [],
          subGenres: []
        }
      }
    }
    // 初始化走默认逻辑
    if (!_lastRevenueType || perType !== context.lastPerType) {
    }
    // total、mobile、pc、console切换
    else if (_lastRevenueType !== revenueType) {
      needFilterValue = getFilterValueWhenRevenueTypeChange();
      // regions和Core Markets切换
    } else if (_lastRegionType !== curRegionType) {
      needFilterValue = getFilterValueWhenRegionTypeChange();
      // 手游的main、sub、main_sub品类筛选器切换
    } else if (_lastGenreType !== curGenreType) {
      needFilterValue = getFilterValueWhenGenreTypeChange();
    }
    setFilterData(needFilterValue);
    return { ...getPageDefaultValue(), ...needFilterValue };
  }

  useEffect(() => {
    setIsTableLoading(true);
    setIsChartLoading(true);
    resetTableAction();
    // setMetrics('revenue');
    setDefaultConfig();
    getFilterData().then((initialFilter: any) => {
      // 继承逻辑中所有的值都从全局存储中拿，原因是perType或revenueType切换的时候，页面上的值是无法拿到的
      initial('init', false, false, inherit(initialFilter, filter?.regionType, filter?.genreType));
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastPerType`,
        payload: perType
      });
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastRevenueType`,
        payload: revenueType
      });
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastRegionType`,
        payload: filter.regionType
      });
      globalDispatch({
        type: `${PAGE_GLOBAL_CONTEXT_NAME}/setLastGenreType`,
        payload: filter.genreType
      });
    });
  }, [filter?.regionType, filter?.genreType]);

  // 页面打点
  const [pageLogInfo, setPageLogInfo] = useState<any>({});
  useEffect(() => {
    const pageKey = `${perType}_${revenueType}`;
    const pageLogConfigKey = PAGE_LOG_CONFIG_KEYS[pageKey];
    const pageLogConfig = dbiPageLogMes[pageLogConfigKey];
    if (!pageLogConfig) return;
    buriedPointPage(pageLogConfig);
    setPageLogInfo(pageLogConfig);
  }, [perType, revenueType]);

  // 更新Search组件 Autocomplete的options的标识
  const updateFlagOfAutoCompleteOptions = useMemo(() => {
    let str = '';
    try {
      str = `${isTableCheckboxHide}_${isCheckboxInit}_${JSON.stringify(tableSelected)}_${JSON.stringify(origianlResData)}`;
    } catch (exception) {
      console.warn(exception);
      str = '';
    }
    return str;
  }, [isTableCheckboxHide, isCheckboxInit, tableSelected, origianlResData]);


  return (
    <div className="dbi-top-market-genre-core">
      <Filter
        isLoading={isTableLoading || isChartLoading}
        perType={perType}
        revenueType={revenueType}
        years={years}
        regionOpts={regionOpts}
        marketOpts={marketOpts}
        genreOpts={genreOpts}
        filter={filter}
        onFilterChange={handleFilterChange}
      />
      <Visualis
        className="dbi-top-market-genre-core_visualis"
        isLoading={isChartLoading}
        perType={perType}
        regionType={filter?.regionType}
        isMobile={isMobile}
        metrics={metrics}
        setMetrics={handleMetricsChange}
        data={uiDatas}
      />
      <Search
        getIcon={getIcon}
        getPageParams={getParamsForSearch}
        renderTableData={renderSearchData}
        searchApi={isMobile ? getGlobalViewTabletDataWhenMobile : getGlobalViewPageDataWhenOther}
        isMobile={isMobile}
        isDodHide={isDodHide}
        setTableLoading={setIsTableLoading}
        isChartLoading={isChartLoading}
        setChartLoading={setIsChartLoading}
        perType={perType}
        revenueType={revenueType}
        metrics={metrics}
        filter={filter}
        regionOpts={regionOpts}
        marketOpts={marketOpts}
        tableAction={tableAction}
        backfillingAfterSearch={backfillingAfterSearch}
        total={total}
        pageLogInfo={pageLogInfo}
        updateFlagOfAutoCompleteOptions={updateFlagOfAutoCompleteOptions}
        onAutoSearchSelect={handleAutoSearchSelect}
        genreOpts={genreOpts}
      />
      <Table
        isMobile={isMobile}
        isLoading={isTableLoading}
        isDodHide={isDodHide}
        isRegionPage={isRegionPage}
        isTableNameCanBeClick={isTableNameCanBeClick}
        isTableCheckboxHide={isTableCheckboxHide}
        records={tableData}
        pageInfo={pageInfo}
        pageSizeOptions={pageSizeOptions}
        total={total}
        tableSelected={tableSelected}
        tableSelectedAll={tableSelectedAll}
        tableMetricsColumns={tableMetricsColumns}
        pageIndex={tableAction.pageIndex}
        pageSize={tableAction.pageSize}
        sortKey={tableAction.sortKey}
        sortOrder={tableAction.sortOrder}
        onNameClick={handleNameClick}
        onTableSortChage={handleTableSortChange}
        onTablePagingChange={handleTablePagingChange}
        onTableCheckboxChange={handleTableCheckboxChange}
      />
    </div>
  );
}

export default connect((state: any) => {
  return {
    context: state[PAGE_GLOBAL_CONTEXT_NAME]
  };
})(CompositePage);
